import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Group,
  Modal,
  Paper,
  Radio,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconCreditCard,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { AddCardDialog } from '@/components/billing/stripe/AddCardDialog';
import { billingApi } from '@/api/billing';
import {
  canAddStripeCard,
  canManageStripeCards,
  stripeConfigUiStateFromResult,
  type StripeConfigUiState,
} from '@/lib/stripeBillingConfig';
import { useEffectivePortalPatientId } from '@/hooks/usePortalSubjectPatientId';
import type { ClientPortalPaymentMethodSummary } from '../../utils/clientPortalFhirTypes';
import {
  formatCardBrand,
  savedPaymentMethodToClientPortalSummary,
} from '../../utils/portalPaymentMethodAdapters';
import {
  BillingActionNotImplementedError,
  removeClientPortalPaymentMethod,
} from '../../api/clientPortalBillingActions';

type CardTableRow = ClientPortalPaymentMethodSummary & {
  nicknameLabel: string;
  expiresLabel: string;
};

function formatExpires(month?: number, year?: number): string {
  if (month == null || year == null) return '—';
  return `${String(month).padStart(2, '0')}/${year}`;
}

export interface ClientPortalCardsSectionProps {
  paymentMethods: ClientPortalPaymentMethodSummary[];
  onError: (err: unknown) => void;
}

export function ClientPortalCardsSection({
  paymentMethods,
  onError,
}: ClientPortalCardsSectionProps): JSX.Element {
  const patientId = useEffectivePortalPatientId();
  const [stripeConfigState, setStripeConfigState] = useState<StripeConfigUiState>({ status: 'loading' });
  const canAddCard = canAddStripeCard(stripeConfigState);
  const canManageCards = canManageStripeCards(stripeConfigState);

  const [cards, setCards] = useState(paymentMethods);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [removingCard, setRemovingCard] = useState<ClientPortalPaymentMethodSummary | null>(null);
  const [pendingDefaultId, setPendingDefaultId] = useState<string | null>(null);

  useEffect(() => {
    setCards(paymentMethods);
  }, [paymentMethods]);

  useEffect(() => {
    let cancelled = false;
    billingApi
      .getStripeConfig()
      .then((result) => {
        if (!cancelled) setStripeConfigState(stripeConfigUiStateFromResult(result));
      })
      .catch(() => {
        if (!cancelled) setStripeConfigState(stripeConfigUiStateFromResult('error'));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const defaultCardId = cards.find((card) => card.isDefault)?.id ?? null;

  const rows = useMemo(
    (): CardTableRow[] =>
      cards.map((card) => ({
        ...card,
        nicknameLabel: card.nickname?.trim() || '—',
        expiresLabel: formatExpires(card.expMonth, card.expYear),
      })),
    [cards],
  );

  const reloadCards = useCallback(async () => {
    const methods = await billingApi.listPaymentMethods(true, patientId ?? undefined);
    setCards(methods.map(savedPaymentMethodToClientPortalSummary));
  }, [patientId]);

  const handleDefaultChange = useCallback(
    async (paymentMethodId: string) => {
      if (paymentMethodId === defaultCardId) return;

      const card = cards.find((item) => item.id === paymentMethodId);
      if (!card) return;

      setPendingDefaultId(paymentMethodId);
      try {
        if (card.stripePaymentMethodId && canManageCards) {
          await billingApi.setDefaultPaymentMethod(card.stripePaymentMethodId, patientId ?? undefined);
          await reloadCards();
          return;
        }

        throw new BillingActionNotImplementedError();
      } catch (err) {
        onError(err);
      } finally {
        setPendingDefaultId(null);
      }
    },
    [
      cards,
      defaultCardId,
      onError,
      patientId,
      reloadCards,
      canManageCards,
    ],
  );

  const handleRemoveConfirm = useCallback(async () => {
    if (!removingCard) return;
    if (removingCard.isDefault || removingCard.id === defaultCardId) return;

    try {
      if (removingCard.stripePaymentMethodId && canManageCards) {
        await billingApi.detachPaymentMethod(
          removingCard.stripePaymentMethodId,
          patientId ?? undefined,
        );
        await reloadCards();
        return;
      }

      await removeClientPortalPaymentMethod(removingCard.id);
      await reloadCards();
    } catch (err) {
      onError(err);
    } finally {
      setRemovingCard(null);
    }
  }, [defaultCardId, onError, patientId, reloadCards, removingCard, canManageCards]);

  const handleCardSaved = useCallback(() => {
    void reloadCards().catch(onError);
  }, [onError, reloadCards]);

  const handleAddCardClick = useCallback(() => {
    if (stripeConfigState.status === 'loading') return;
    if (canAddCard) {
      setAddCardOpen(true);
      return;
    }
    onError(new BillingActionNotImplementedError());
  }, [canAddCard, onError, stripeConfigState.status]);

  const columns = useMemo(
    (): UnifyTableColumn<CardTableRow>[] => [
      {
        key: 'nicknameLabel',
        label: 'Nickname',
        render: (row) => (
          <Text size="sm" fw={600}>
            {row.nicknameLabel}
          </Text>
        ),
        getValue: (row) => row.nicknameLabel,
      },
      {
        key: 'label',
        label: 'Card',
        render: (row) => (
          <Group gap="xs" wrap="nowrap">
            <IconCreditCard size={16} color="var(--mantine-color-gray-6)" />
            <Text size="sm" fw={500}>
              {row.label}
            </Text>
          </Group>
        ),
        getValue: (row) => row.label,
      },
      {
        key: 'expiresLabel',
        label: 'Expires',
        render: (row) => <Text size="sm" c="gray.6">{row.expiresLabel}</Text>,
        getValue: (row) => row.expiresLabel,
        width: 100,
      },
      {
        key: 'default',
        label: 'Default',
        sortable: false,
        render: (row) => (
          <Radio
            checked={row.id === defaultCardId}
            disabled={pendingDefaultId != null || !canManageCards}
            onChange={() => void handleDefaultChange(row.id)}
            aria-label={`Set ${row.nickname?.trim() || row.label} as the default card`}
          />
        ),
        getValue: (row) => (row.id === defaultCardId ? 'Yes' : 'No'),
        width: 90,
        align: 'center',
      },
      {
        key: 'actions',
        label: '',
        sortable: false,
        pinRight: true,
        render: (row) => {
          const isOnlyCard = cards.length === 1;
          const isDefault = row.id === defaultCardId;
          const removeDisabled = isOnlyCard || isDefault || !canManageCards;
          const tooltipLabel = !canManageCards
            ? 'Card management is unavailable until Stripe is configured.'
            : isOnlyCard
              ? 'Add another card before removing your only payment method.'
              : 'Select a new default card before you can remove this one.';

          return (
            <Tooltip
              label={tooltipLabel}
              disabled={!removeDisabled}
              withArrow
            >
              <span>
                <Button
                  size="xs"
                  variant="light"
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => setRemovingCard(row)}
                  disabled={removeDisabled}
                >
                  Remove
                </Button>
              </span>
            </Tooltip>
          );
        },
        getValue: () => '',
      },
    ],
    [cards.length, defaultCardId, handleDefaultChange, pendingDefaultId, canManageCards],
  );

  const emptyState = (
    <Paper withBorder p="lg" radius="md">
      <Stack align="center" gap="md" py="xl">
        <IconCreditCard size={36} color="var(--mantine-color-gray-5)" />
        <Stack gap={4} align="center">
          <Text fw={600}>No cards on file</Text>
          <Text size="sm" c="dimmed" ta="center" maw={360}>
            The first card you add will be set as your default payment method.
          </Text>
        </Stack>
        <Button
          color="gold"
          leftSection={<IconPlus size={16} />}
          onClick={handleAddCardClick}
          disabled={!canAddCard}
        >
          Add card
        </Button>
      </Stack>
    </Paper>
  );

  if (cards.length === 0) {
    return (
      <>
        <Stack gap="md">
          <div>
            <Text fw={700} size="lg">
              Cards
            </Text>
            <Text size="sm" c="dimmed" mt={4}>
              Add as many cards as you need, give them nicknames, and choose a default.
            </Text>
          </div>
          {emptyState}
        </Stack>

        <AddCardDialog
          opened={addCardOpen}
          onClose={() => setAddCardOpen(false)}
          onCardSaved={handleCardSaved}
          patientId={patientId ?? undefined}
        />
      </>
    );
  }

  return (
    <>
      <UnifyTable
        title="Cards"
        subtitle="Add as many cards as you need, give them nicknames, and choose a default."
        columns={columns}
        data={rows}
        getRowKey={(row) => row.id}
        pagination={false}
        sortable={false}
        headerRight={
          <Button
            color="gold"
            size="sm"
            leftSection={<IconPlus size={16} />}
            onClick={handleAddCardClick}
            disabled={!canAddCard}
          >
            Add card
          </Button>
        }
      />

      <Modal
        opened={removingCard != null}
        onClose={() => setRemovingCard(null)}
        title="Remove card"
        centered
      >
        {removingCard && (
          <Stack gap="md">
            <Text size="sm">
              Remove{' '}
              <Text span fw={600}>
                {removingCard.nickname?.trim() || formatCardBrand(removingCard.brand ?? 'Card')}{' '}
                {removingCard.last4 ? `•••• ${removingCard.last4}` : removingCard.label}
              </Text>
              ? This card will no longer be available for payments.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={() => setRemovingCard(null)}>
                Cancel
              </Button>
              <Button color="red" onClick={() => void handleRemoveConfirm()}>
                Remove card
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <AddCardDialog
        opened={addCardOpen}
        onClose={() => setAddCardOpen(false)}
        onCardSaved={handleCardSaved}
        patientId={patientId ?? undefined}
      />
    </>
  );
}
